import { motion } from "framer-motion";
import {
  ArrowLeft,
  
  Sandwich,
  Utensils,
  Store,
  MapPin,
  Wheat,
  Beer,
  Wine,
  Smile,
  UtensilsCrossed,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";

const SITE = "https://cphhighlights.com";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/fuwjhfpl_20260823_083044.webp";

const SECOND_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/xs7lrasl_20260823_083049%280%29.webp";

const SITDOWN = [
  { name: "Restaurant Schønnemann", area: "Indre By", address: "Hauser Pl. 16, 1127 København", body: "The legendary old-town institution — cellar dining, hundreds of snaps and by far the most famous smørrebrød address in town. Book ahead." },
  { name: "Aamanns 1921", area: "Indre By", address: "Niels Hemmingsens Gade 19–21, 1153 København", body: "A modern, refined take on the classics with beautifully sourced ingredients — a lovely first introduction." },
  { name: "Restaurant Kronborg", area: "Indre By", address: "Brolæggerstræde 12, 1211 København", body: "Cosy, traditional and honest, tucked just off the old town — a local favourite for a proper lunch." },
  { name: "Café Sorgenfri", area: "Indre By", address: "Brolæggerstræde 8, 1211 København", body: "A no-frills, proudly old-school basement kitchen — dark rye, herring and cold beer just as it's always been. Deeply local." },
  { name: "Told & Snaps", area: "By the harbour", address: "Toldbodgade 2, 1253 København", body: "Old-school Danish lunch restaurant heavy on herring, aquavit and atmosphere." },
  { name: "Nyhavns Færgekro", area: "Nyhavn", address: "Nyhavn 5, 1051 København", body: "Right on the postcard canal — a classic herring buffet and open sandwiches with a view." },
  { name: "Christianshavns Færgecafé", area: "Christianshavn", address: "Strandgade 50, 1401 København", body: "A charming spot across the water, known for generous, well-made smørrebrød." },
];

const CASUAL = [
  { name: "Hallernes Smørrebrød", area: "Torvehallerne & Tivoli Food Hall", body: "The same much-loved smørrebrød counter in two spots — the glass food market at Torvehallerne and the Tivoli Food Hall. Grab a single piece or a few to taste your way through, no booking needed." },
  { name: "Rørt", area: "Broens Gadekøkken · Torvehallerne · Bernikow Gården", body: "A modern smørrebrød concept with several central spots — a relaxed, contemporary way to taste it without a full sit-down lunch." },
];

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Smørrebrød", item: `${SITE}/smorrebrod` },
  ],
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Where to Try Smørrebrød in Copenhagen — Best Places to Eat It",
  url: `${SITE}/smorrebrod`,
  description:
    "The best places to eat smørrebrød in Copenhagen — classic sit-down restaurants (Schønnemann, Aamanns 1921, Kronborg), casual tasting spots (Hallernes in Torvehallerne, Tivoli Food Hall, Rørt), and how to eat it with beer and snaps.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

function PlaceCard({ p, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-zinc-200 shadow-brut p-5 h-full flex items-start gap-4">
      <div className="w-10 h-10 flex items-center justify-center text-white shrink-0" style={{ backgroundColor: accent }}>
        <Icon size={19} />
      </div>
      <div className="flex-1">
        <h3 className="font-display font-900 tracking-tight text-lg leading-tight">
          {p.name} <span className="text-black/40 font-editorial italic font-600 text-base">· {p.area}</span>
        </h3>
        <p className="font-body text-sm text-black/70 mt-1.5 leading-relaxed">{p.body}</p>
        {p.address && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${p.name}, ${p.address}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`smorrebrod-map-${p.name.toLowerCase().replace(/[^a-z]/g, "")}`}
            className="inline-flex items-center gap-1.5 font-body font-700 text-xs text-[#DF3226] mt-3 hover:gap-2.5 transition-all"
          >
            <MapPin size={13} /> {p.address}
          </a>
        )}
      </div>
    </div>
  );
}

export default function SmorrebrodPage() {
  return (
    <main data-testid="smorrebrod-page" className="pt-16 md:pt-20">
      <Seo
        title="Where to eat Smørrebrød in Copenhagen"
        description="The best smørrebrød in Copenhagen — classic restaurants like Schønnemann and Aamanns 1921, plus casual spots in Torvehallerne and Tivoli Food Hall."
        path="/smorrebrod"
        image={HERO_IMG}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Danish smørrebrød open sandwiches" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="smorrebrod-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="smorrebrod-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">THE DANISH CLASSIC</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Smørrebrød<br />
            <span className="font-editorial italic font-600">the open sandwich</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6 leading-relaxed">
            Denmark&apos;s most iconic lunch: a slice of dense rye bread piled high with everything from
            pickled herring to roast pork, prawns or fried fish, finished with pickles, mayo and herbs.
            Here&apos;s what it is, where to try it, and how the locals do it.
          </p>
        </div>
      </section>

      {/* what it is + rye bread */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <div className="flex items-center gap-3 mb-3">
                <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">START WITH THE BREAD</p>
              </div>
              <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
                It all begins with<br />
                <span className="font-editorial italic font-600">rugbrød</span>
              </h2>
              <p className="font-body text-base md:text-lg text-black/70 mt-6 leading-relaxed">
                The foundation of every smørrebrød is <strong>rugbrød</strong> — a dense, dark Danish rye
                sourdough packed with whole grains and seeds. It&apos;s slightly sour, seriously filling and
                a genuine everyday staple: Danes grow up eating it daily. It&apos;s a world away from soft
                white sandwich bread, which is exactly the point — its deep, hearty flavour is built to carry
                bold, salty toppings.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-zinc-200 shadow-brut overflow-hidden aspect-[4/3]">
                <img src={SECOND_IMG} alt="Danish smørrebrød on dark rye bread" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* sit-down places */}
      <section className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">THE CLASSIC SIT-DOWN LUNCH</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
              Where to sit down in Copenhagen<br />
              <span className="font-editorial italic font-600">for the real thing</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 mt-6 max-w-2xl leading-relaxed">
              Smørrebrød is a lunch dish — most of these are open midday only, and the classics fill up, so
              book ahead. Order a few pieces and eat them in the traditional order: herring first, then fish,
              then meat.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {SITDOWN.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.05} y={30}>
                <PlaceCard p={p} icon={Utensils} accent="#DF3226" />
              </Reveal>
            ))}
            <Reveal y={30}>
              <Link
                to="/advertise"
                data-testid="smorrebrod-missing"
                className="group bg-white border-2 border-dashed border-zinc-300 hover:border-[#DF3226] hover:-translate-y-1 transition-all p-5 h-full flex flex-col"
              >
                <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                  <Utensils size={12} /> THIS SPOT IS OPEN
                </span>
                <h3 className="font-display font-900 tracking-tight text-xl mt-3 leading-none">
                  Is your smørrebrød place missing?
                </h3>
                <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">
                  Put it in front of travellers choosing tomorrow&apos;s lunch.
                </p>
                <span className="mt-auto pt-3 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] group-hover:gap-2.5 transition-all">
                  Advertise with us
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* casual / tasting */}
      <section className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">JUST WANT A TASTE?</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
              Casual spots<br />
              <span className="font-editorial italic font-600">to try a piece or two</span>
            </h2>
            <p className="font-body text-base md:text-lg text-black/70 mt-6 max-w-2xl leading-relaxed">
              Not sure you&apos;ll love it? Skip the full lunch and just taste one — these market and
              food-hall counters let you grab a single piece, no booking required.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {CASUAL.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.05} y={30}>
                <PlaceCard p={p} icon={Store} accent="#0E7C5A" />
              </Reveal>
            ))}
          </div>

          <Reveal y={20}>
            <Link
              to="/advertise"
              data-testid="smorrebrod-missing"
              className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white border-2 border-dashed border-zinc-300 hover:border-[#DF3226] hover:-translate-y-0.5 transition-all p-5 md:p-6"
            >
              <div>
                <span className="inline-flex items-center gap-1.5 font-body font-800 text-[11px] tracking-widest text-[#DF3226]">
                  <UtensilsCrossed size={12} /> THIS SPOT IS OPEN
                </span>
                <h3 className="font-display font-900 tracking-tight text-xl leading-none mt-2">
                  Is your smørrebrød place missing?
                </h3>
                <p className="font-body text-sm text-black/70 mt-1.5">
                  Stacking rye for the lunch crowd? Get in front of the travellers planning their
                  Copenhagen lunches.
                </p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226]">
                Advertise with us
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* how the locals eat it: beer & snaps */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">HOW THE LOCALS DO IT</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              A beer, a snaps<br />
              <span className="font-editorial italic font-600">and no rush</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            <Reveal y={30}>
              <div className="bg-white/[0.06] border border-white/15 p-6 h-full flex items-start gap-4 rounded-2xl">
                <div className="w-11 h-11 flex items-center justify-center bg-[#DF3226] text-white shrink-0 rounded-xl">
                  <Beer size={20} />
                </div>
                <div>
                  <h3 className="font-display font-900 tracking-tight text-xl">A cold beer</h3>
                  <p className="font-body text-sm text-white/70 mt-1.5 leading-relaxed">
                    Traditionally washed down with a crisp Danish lager (a Carlsberg or Tuborg does the job).
                    The bubbles and bitterness cut through all that salty herring and rich mayo.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal y={30} delay={0.08}>
              <div className="bg-white/[0.06] border border-white/15 p-6 h-full flex items-start gap-4 rounded-2xl">
                <div className="w-11 h-11 flex items-center justify-center bg-[#DF3226] text-white shrink-0 rounded-xl">
                  <Wine size={20} />
                </div>
                <div>
                  <h3 className="font-display font-900 tracking-tight text-xl">...and a shot of snaps</h3>
                  <p className="font-body text-sm text-white/70 mt-1.5 leading-relaxed">
                    <strong>Snaps</strong> (also called akvavit) is a strong Scandinavian spirit — around 40%,
                    distilled from grain or potatoes and flavoured with caraway or dill. It&apos;s served
                    ice-cold in a small glass and sipped between bites, often with a hearty &quot;skål!&quot;
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* honest / fun note */}
      <section className="bg-[#FCEFC5] text-black border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
          <Reveal>
            <div className="flex items-start gap-4 max-w-3xl">
              <div className="w-11 h-11 flex items-center justify-center bg-black text-[#DF3226] shrink-0 rounded-xl">
                <Smile size={20} />
              </div>
              <div>
                <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-2xl md:text-3xl">
                  A little honesty, though...
                </h2>
                <p className="font-body text-base md:text-lg text-black/80 mt-3 leading-relaxed">
                  We&apos;ll be straight with you: plenty of tourists try smørrebrød and quietly decide it
                  wasn&apos;t quite for them. A lot of it comes down to the bread — most of the world grows up
                  on soft white loaves, and that dense, sour rye can be a shock on the first bite (never mind
                  the pickled herring). Our advice? Go in curious, start with a milder topping like roast pork
                  or the classic prawn, and treat it as tasting a piece of living Danish history — even if you
                  end up back at the bakery for a cinnamon bun afterwards.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Smørrebrød sorted?<br />
            <span className="font-editorial italic font-600">Now build your days</span>
          </h2>
          <Link
            to="/planner"
            data-testid="smorrebrod-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      {/* bookable tour — the Copenhagen tasting tour */}
      <GygTourBand tourId="21977" label="BOOK A TOUR" />
      <ExploreLinks
        testid="explore-smorrebrod"
        links={[
          { label: "The full table — the Danish food guide", to: "/danish-food" },
          { label: "Where to walk it off — Christianshavn area guide", to: "/areas/christianshavn" },
          { label: "Something sweet after — the bakery guide", to: "/bakeries" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
